import { useTranslation } from 'react-i18next';
import { Details, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import { ChatExclamationmarkIcon, HandshakeIcon, HeartIcon, PersonGroupIcon, ShieldLockIcon } from '@navikt/aksel-icons';
import type { CategoryInsight, QuestionInsight } from '../../../../api/queries';
import {
  formatScore,
  lowestQuestions,
  scoreLevel,
  scorePercent,
  weakestFirst,
} from '../hrPsychologicalSafetyView';
import { agreementOf } from '../../../../domain/psychological-safety/team-result';
import { agreementTones } from '../../../../components/AnalysisLayout/analysis.utils';
import classes from '../HRPsychologicalSafety.module.css';

const categoryIcon = (category: string) => {
  const name = category.toLowerCase();
  if (name.includes('inkludering') || name.includes('inclusion')) return PersonGroupIcon;
  if (name.includes('tillit') || name.includes('trust')) return HandshakeIcon;
  if (name.includes('anerkjennelse') || name.includes('recognition')) return HeartIcon;
  if (name.includes('trygghet') || name.includes('safety')) return ChatExclamationmarkIcon;
  return ShieldLockIcon;
};

const ScoreBar = ({ score }: { score: number }) => (
  <span aria-hidden="true" className={classes.track}>
    <span className={classes.fill} data-level={scoreLevel(score)} style={{ width: `${scorePercent(score)}%` }} />
  </span>
);

type AreaListProps = {
  categories: CategoryInsight[];
  questions: QuestionInsight[];
  teamsWithResults: number;
};

/** De fire områdene, svakest først, og spørsmålene med lavest skår */
export const AreaList = ({ categories, questions, teamsWithResults }: AreaListProps) => {
  const { t, i18n } = useTranslation();
  const areas = weakestFirst(categories);
  const lowest = lowestQuestions(questions);

  return (
    <section aria-labelledby="hr-ps-areas-heading" id="hr-ps-areas" className={classes.section}>
      <div className={classes.sectionIntro}>
        <Heading level={2} data-size="md" id="hr-ps-areas-heading">
          {t('hrPsychologicalSafety.areas.title', { count: areas.length })}
        </Heading>
        <Paragraph className={classes.subtle}>
          {t('hrPsychologicalSafety.areas.intro', { count: teamsWithResults })}
        </Paragraph>
      </div>
      <ul className={`${classes.card} ${classes.areaList}`}>
        {areas.map((area, index) => {
          const Icon = categoryIcon(area.category);
          const agreement = agreementOf(area.averageStandardDeviation);
          return (
            <li key={area.category} className={classes.area}>
              <span aria-hidden="true" className={classes.areaIcon}>
                <Icon aria-hidden />
              </span>
              <div className={classes.areaBody}>
                <div className={classes.areaHeader}>
                  <Heading level={3} data-size="xs" className={classes.areaTitle}>
                    {area.category}
                    {index === 0 && areas.length > 1 && (
                      <Tag data-size="sm" data-color="warning">
                        {t('hrPsychologicalSafety.areas.lowest')}
                      </Tag>
                    )}
                  </Heading>
                  <span className={classes.areaScore}>
                    <span className={classes.areaScoreValue}>{formatScore(area.averageScore, i18n.language)}</span>{' '}
                    {t('hrPsychologicalSafety.summary.of')}
                  </span>
                </div>
                <ScoreBar score={area.averageScore} />
                {area.description && <Paragraph data-size="sm">{area.description}</Paragraph>}
                <div className={classes.areaMeta}>
                  {agreement && (
                    <Tag data-size="sm" data-color={agreementTones[agreement]}>
                      {t(`teamResult.agreement.${agreement}`)}
                    </Tag>
                  )}
                  <span className={classes.subtle}>
                    {agreement &&
                      `${t(`teamResult.agreementAcrossTeams.${agreement}`)} (${formatScore(
                        area.averageStandardDeviation ?? 0,
                        i18n.language,
                        2,
                      )}) · `}
                    {t('hrPsychologicalSafety.areas.teams', { count: area.teamCount })}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {lowest.length > 0 && (
        <Details>
          <Details.Summary>{t('hrPsychologicalSafety.areas.lowestQuestions')}</Details.Summary>
          <Details.Content>
            <ul className={classes.questionList}>
              {lowest.map((question) => (
                <li key={question.question} className={classes.questionRow}>
                  <span>{question.question}</span>
                  <ScoreBar score={question.averageScore} />
                  <span className={classes.questionScore}>{formatScore(question.averageScore, i18n.language)}</span>
                </li>
              ))}
            </ul>
          </Details.Content>
        </Details>
      )}
    </section>
  );
};
